import { useState } from "react";
import { Quote, ChevronDown, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import ClientTicker from "@/components/ClientTicker";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES, TESTIMONIALS } from "@/lib/content";

export default function CaseStudies() {
  const [open, setOpen] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);

  return (
    <div data-testid="case-studies-page">
      <PageHero
        eyebrow="Case studies & testimonials"
        title="Outcomes our clients can measure"
        description="Real change you have achieved shows up in the numbers — reporting that recovers, interventions that triple, injuries that dramatically fall or are eliminated. Explore how our work performs across high-hazard sectors."
      />

      <ClientTicker />

      <section className="bg-slate-50 py-20 lg:py-32">
        <div className="container-arms">
          <Reveal>
            <p className="mb-10 inline-block rounded-full bg-teal/10 px-4 py-2 text-xs font-semibold text-teal" data-testid="case-studies-sample-note">
              Working in partnership with our clients since 2005
            </p>
          </Reveal>
          <div className="space-y-6">
            {CASE_STUDIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid={`case-study-${i}`}>
                  <button
                    className="flex w-full items-center gap-6 p-6 text-left sm:p-8"
                    onClick={() => setOpen(open === i ? -1 : i)}
                    aria-expanded={open === i}
                    data-testid={`case-study-toggle-${i}`}
                  >
                    <img src={c.image} alt="" loading="lazy" className="hidden h-20 w-28 shrink-0 rounded-2xl object-cover img-duotone sm:block" />
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-teal">{c.sector}</p>
                      {c.logo && <img src={c.logo} alt="" loading="lazy" className="mt-2 h-6 w-auto object-contain" data-testid={`case-study-logo-${i}`} />}
                      <h2 className="mt-1 text-lg sm:text-xl font-bold tracking-tight text-navy">{c.title}</h2>
                    </div>
                    <div className="hidden shrink-0 text-right md:block">
                      <p className="font-display text-2xl sm:text-3xl font-extrabold text-teal">{c.metric}</p>
                      <p className="text-xs text-slate-500 max-w-[180px]">{c.metricLabel}</p>
                    </div>
                    <ChevronDown size={22} className={`shrink-0 text-slate-400 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                  </button>
                  {open === i && (
                    <div className="border-t border-slate-100 p-6 sm:p-8" data-testid={`case-study-detail-${i}`}>
                      <div className="grid gap-8 md:grid-cols-3">
                        <div>
                          <h3 className="eyebrow mb-3">The challenge</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{c.problem}</p>
                        </div>
                        <div>
                          <h3 className="eyebrow mb-3">Our partnership</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{c.intervention}</p>
                        </div>
                        <div className="rounded-2xl bg-teal-light p-6 md:row-span-2">
                          <h3 className="eyebrow mb-3">The result</h3>
                          <p className="font-display text-xl sm:text-2xl font-extrabold text-teal leading-snug">{c.resultHighlight || c.metric}</p>
                          <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.result}</p>
                          {c.resultImage && (
                            <button
                              type="button"
                              onClick={() => setZoomedImage(c.resultImage)}
                              aria-label="Open the results graph full size"
                              data-testid={`case-study-result-image-button-${i}`}
                              className="mt-4 block w-full cursor-zoom-in"
                            >
                              <img src={c.resultImage} alt="BOSS programme results graph — click to view full size" data-testid={`case-study-result-image-${i}`} className="w-full rounded-xl border border-slate-200 bg-white" />
                            </button>
                          )}
                          {c.client && <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{c.client}</p>}
                        </div>
                        {c.video && (
                          <div className="md:col-span-2" data-testid={`case-study-video-${i}`}>
                            <h3 className="eyebrow mb-3">Watch the film</h3>
                            <video
                              poster={c.videoPoster}
                              controls
                              preload="metadata"
                              playsInline
                              className="w-full rounded-2xl border border-slate-200"
                            >
                              {c.videoWebm && <source src={c.videoWebm} type="video/webm" />}
                              <source src={c.video} type="video/mp4" />
                            </video>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-32" data-testid="testimonial-wall">
        <div className="container-arms">
          <Reveal>
            <p className="eyebrow mb-4">What clients say</p>
            <h2 className="max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">Trusted by the people responsible for delivering excellence</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name + i} delay={i * 0.07}>
                <figure className="relative flex h-full flex-col rounded-3xl border border-slate-200 bg-slate-50 p-8 lg:p-10" data-testid={`testimonial-${i}`}>
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <Quote className="text-teal" size={28} aria-hidden="true" />
                    {t.logo && <img src={t.logo} alt={t.logoAlt || ""} loading="lazy" className="h-8 w-auto max-w-[110px] object-contain" data-testid={`testimonial-logo-${i}`} />}
                  </div>
                  <blockquote className="flex-1 text-base sm:text-lg font-medium leading-relaxed text-slate-800">"{t.quote}"</blockquote>
                  <figcaption className="mt-6 lg:whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.1em] text-teal">
                    {t.name} — {t.org}{" "}
                    {t.orgNote && <span className="text-[9px] normal-case tracking-normal text-slate-400">{t.orgNote}</span>}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Become our next measurable outcome" text="Every engagement is baselined, measured and reported — so you can prove the change, not just feel it." />

      {zoomedImage && (
        <div
          data-testid="result-image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Results graph, full size"
          className="fixed inset-0 z-[100] flex cursor-zoom-out items-center justify-center bg-navy/85 p-4 backdrop-blur-sm sm:p-10"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt="BOSS programme results graph — full size"
            data-testid="result-image-lightbox-image"
            className="max-h-full max-w-full cursor-default rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Close"
            data-testid="result-image-lightbox-close"
            onClick={() => setZoomedImage(null)}
            className="absolute right-5 top-5 rounded-full bg-white/90 p-2 text-navy shadow-md transition-colors hover:bg-white"
          >
            <X size={22} />
          </button>
        </div>
      )}
    </div>
  );
}
